import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Tabs,
  Tab,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  IconButton,
  Paper,
  Checkbox,
  Link,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  Fade
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GoogleIcon from '@mui/icons-material/Google';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ModalPortal from './ModalPortal';
import { registerCustomer, registerCompany, registerPartner, login } from '../services/api';

const AuthModal = ({ open, onClose, mode }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(mode === 'login' ? 0 : 1);
  const [userType, setUserType] = useState('customer');
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    contactName: '',
    specialization: '',
    acceptTerms: false,
    acceptPrivacy: false,
    acceptMarketing: false
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Reset form when modal is opened
  useEffect(() => {
    if (open) {
      setRegistrationSuccess(false);
      setLoading(false);
    }
  }, [open]);
  
  // Update snackbar message if language changes and there's an active message
  useEffect(() => {
    if (snackbar.open && snackbar.translationKey) {
      setSnackbar(prev => ({
        ...prev,
        message: t(prev.translationKey)
      }));
    }
  }, [i18n.language, t, snackbar.open, snackbar.translationKey]);
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const handleUserTypeChange = (event) => {
    setUserType(event.target.value);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleCheckboxChange = (event) => {
    setFormData(prev => ({
      ...prev,
      [event.target.name]: event.target.checked
    }));
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  // Update the setSnackbar function to also store the translation key
  const showSnackbar = (translationKey, defaultMessage, severity = 'error') => {
    setSnackbar({
      open: true,
      message: t(translationKey, defaultMessage),
      severity: severity,
      translationKey: translationKey // Store the key for language changes
    });
  };
  
  const validateForm = () => {
    if (activeTab === 1) {
      // Registration validation
      if (!formData.email || !formData.password) {
        showSnackbar('auth.validation.emailPasswordRequired', 'Email and password are required');
        return false;
      }
      
      if (formData.password !== formData.confirmPassword) {
        showSnackbar('auth.validation.passwordsDoNotMatch', 'Passwords do not match');
        return false;
      }
      
      if (userType === 'customer' && (!formData.firstName || !formData.lastName)) {
        showSnackbar('auth.validation.nameRequired', 'First name and last name are required');
        return false;
      }
      
      if (userType === 'business' && !formData.name) {
        showSnackbar('auth.validation.companyNameRequired', 'Company name is required');
        return false;
      }
      
      if (userType === 'partner' && !formData.name) {
        showSnackbar('auth.validation.partnerNameRequired', 'Partner name is required');
        return false;
      }
      
      if (!formData.acceptTerms || !formData.acceptPrivacy) {
        showSnackbar('auth.termsRequired', 'You must accept the terms and privacy policy to register');
        return false;
      }
    } else {
      // Login validation
      if (!formData.email || !formData.password) {
        showSnackbar('auth.validation.emailPasswordRequired', 'Email and password are required');
        return false;
      }
    }
    
    return true;
  };
  
  const redirectToDashboard = (userType) => {
    console.log('Redirecting to dashboard for user type:', userType);
    
    // Redirect based on user type
    setTimeout(() => {
      onClose();
      if (userType === 'customer') {
        console.log('Navigating to customer dashboard');
        navigate('/panel/customer-dashboard');
      } else if (userType === 'business' || userType === 'company') {
        console.log('Navigating to company dashboard');
        navigate('/panel/company-dashboard');
      } else if (userType === 'partner') {
        console.log('Navigating to partner dashboard');
        navigate('/panel/partner-dashboard');
      }
    }, 2000); // Wait 2 seconds before redirecting
  };
  
  const handleSubmit = async (event) => {
    event.preventDefault();
    console.log('Form submission triggered');
    
    if (!validateForm()) {
      console.log('Form validation failed');
      return;
    }
    
    console.log('Form is valid, proceeding with submission');
    setLoading(true);
    
    try {
      if (activeTab === 0) {
        // Login logic
        console.log("Attempting login with:", formData.email, formData.password);
        
        const response = await login(formData.email, formData.password);
        console.log("Login response:", response);
        
        if (response.success) {
          showSnackbar('auth.login.success', 'Login successful', 'success');
          
          // After successful login, redirect to the appropriate dashboard
          redirectToDashboard(response.user.role);
        } else {
          throw new Error(response.message || 'Error logging in');
        }
      } else {
        // Registration logic based on user type
        if (userType === 'customer') {
          console.log('Registering customer with data:', formData);
          const customerData = {
            email: formData.email,
            password: formData.password,
            firstName: formData.firstName,
            lastName: formData.lastName
          };
          
          try {
            const response = await registerCustomer(customerData);
            console.log('Customer registration response:', response);
            
            if (response.success) {
              setRegistrationSuccess(true);
              showSnackbar('auth.register.success', 'Registration successful', 'success');
              
              // Only redirect after successful registration
              redirectToDashboard('customer');
            } else {
              throw new Error(response.message || 'Error registering customer');
            }
          } catch (error) {
            throw error; // Let the outer catch block handle it
          }
        } else if (userType === 'business') {
          const companyData = {
            email: formData.email,
            password: formData.password,
            companyName: formData.name
          };
          
          try {
            const response = await registerCompany(companyData);
            
            if (response.success) {
              setRegistrationSuccess(true);
              showSnackbar('auth.register.success', 'Registration successful', 'success');
              
              // Only redirect after successful registration
              redirectToDashboard('business');
            } else {
              throw new Error(response.message || 'Error registering company');
            }
          } catch (error) {
            throw error; // Let the outer catch block handle it
          }
        } else if (userType === 'partner') {
          const partnerData = {
            email: formData.email,
            password: formData.password,
            partnerName: formData.name
          };
          
          try {
            const response = await registerPartner(partnerData);
            
            if (response.success) {
              setRegistrationSuccess(true);
              showSnackbar('auth.register.success', 'Registration successful', 'success');
              
              // Only redirect after successful registration
              redirectToDashboard('partner');
            } else {
              throw new Error(response.message || 'Error registering partner');
            }
          } catch (error) {
            throw error; // Let the outer catch block handle it
          }
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
      
      let errorTranslationKey = 'auth.errors.general';
      
      if (error.response) {
        if (error.response.status === 409) {
          // Check if the error message specifies what type of account the email is registered as
          if (error.response.data && error.response.data.message) {
            // Match error message regardless of language
            const errorMessageLower = error.response.data.message.toLowerCase();
            
            if (errorMessageLower.includes('customer') || errorMessageLower.includes('cliente')) {
              errorTranslationKey = 'auth.errors.emailRegisteredAsCustomer';
            } else if (errorMessageLower.includes('company') || errorMessageLower.includes('empresa')) {
              errorTranslationKey = 'auth.errors.emailRegisteredAsCompany';
            } else if (errorMessageLower.includes('partner') || errorMessageLower.includes('socio')) {
              errorTranslationKey = 'auth.errors.emailRegisteredAsPartner';
            } else {
              errorTranslationKey = 'auth.errors.emailExists';
            }
          } else {
            errorTranslationKey = 'auth.errors.emailExists';
          }
        } else if (error.response.status === 401) {
          errorTranslationKey = 'auth.errors.invalidCredentials';
        } else if (error.response.data && error.response.data.message) {
          // For non-translated backend messages, just use them directly
          showSnackbar('', error.response.data.message, 'error');
          setLoading(false);
          return;
        }
      } else if (error.message) {
        // For non-translated error messages, just use them directly
        showSnackbar('', error.message, 'error');
        setLoading(false);
        return;
      }
      
      showSnackbar(errorTranslationKey, 'An error occurred', 'error');
    } finally {
      if (!registrationSuccess) {
        setLoading(false);
      }
    }
  };
  
  return (
    <ModalPortal open={open}>
      <Dialog 
        open={open} 
        onClose={!loading && !registrationSuccess ? onClose : null}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            px: { xs: 2, sm: 4 },
            py: 2
          }
        }}
        container={document.getElementById('modal-root')}
        disableEnforceFocus
        hideBackdrop={false}
      >
        {registrationSuccess ? (
          <Fade in={registrationSuccess}>
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CheckCircleOutlineIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                {t('auth.register.successTitle')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('auth.register.successMessage')}
              </Typography>
              <CircularProgress size={24} sx={{ mt: 2 }} />
            </Box>
          </Fade>
        ) : (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
              <Typography variant="h5" component="div" fontWeight="bold">
                {activeTab === 0 ? t('header.login') : t('header.register')}
              </Typography>
              <IconButton onClick={onClose} aria-label="close" disabled={loading}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={activeTab} onChange={handleTabChange} centered>
                <Tab label={t('header.login')} />
                <Tab label={t('header.register')} />
              </Tabs>
            </Box>
            
            <DialogContent>
              <Box 
                component="form" 
                id="auth-form"
                onSubmit={handleSubmit} 
                sx={{ mt: 2 }}
              >
                {activeTab === 1 && (
                  <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                    <FormLabel component="legend">{t('auth.accountType', 'Tipo de cuenta')}</FormLabel>
                    <RadioGroup
                      row
                      name="user-type"
                      value={userType}
                      onChange={handleUserTypeChange}
                    >
                      <FormControlLabel value="customer" control={<Radio />} label={t('auth.client')} />
                      <FormControlLabel value="business" control={<Radio />} label={t('auth.business')} />
                      <FormControlLabel value="partner" control={<Radio />} label={t('auth.partner')} />
                    </RadioGroup>
                  </FormControl>
                )}
                
                {activeTab === 1 && userType === 'customer' && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        margin="dense"
                        label={t('auth.firstName', 'Nombre')}
                        name="firstName"
                        type="text"
                        fullWidth
                        variant="outlined"
                        required
                        value={formData.firstName}
                        onChange={handleInputChange}
                        sx={{ mb: 2 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        margin="dense"
                        label={t('auth.lastName', 'Apellido')}
                        name="lastName"
                        type="text"
                        fullWidth
                        variant="outlined"
                        required
                        value={formData.lastName}
                        onChange={handleInputChange}
                        sx={{ mb: 2 }}
                      />
                    </Grid>
                  </Grid>
                )}
                
                {activeTab === 1 && userType !== 'customer' && (
                  <TextField
                    margin="dense"
                    label={userType === 'business' ? t('auth.companyName', 'Nombre de la empresa') : t('auth.gymName', 'Nombre del gimnasio')}
                    name="name"
                    type="text"
                    fullWidth
                    variant="outlined"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    sx={{ mb: 2 }}
                  />
                )}
                
                <TextField
                  margin="dense"
                  label={t('auth.email', 'Email')}
                  name="email"
                  type="email"
                  fullWidth
                  variant="outlined"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  sx={{ mb: 2 }}
                />
                
                <TextField
                  margin="dense"
                  label={t('auth.password', 'Contraseña')}
                  name="password"
                  type="password"
                  fullWidth
                  variant="outlined"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  sx={{ mb: 2 }}
                />
                
                {activeTab === 1 && (
                  <TextField
                    margin="dense"
                    label={t('auth.confirmPassword', 'Confirmar contraseña')}
                    name="confirmPassword"
                    type="password"
                    fullWidth
                    variant="outlined"
                    required
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    sx={{ mb: 2 }}
                  />
                )}
                
                {activeTab === 1 && (
                  <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          name="acceptTerms" 
                          checked={formData.acceptTerms} 
                          onChange={handleCheckboxChange}
                          required
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {t('signup.terms.accept', 'Acepto los ')}
                          <Link href="/terms" target="_blank">{t('signup.terms.name', 'Términos y Condiciones')}</Link>
                        </Typography>
                      }
                    />
                    
                    <FormControlLabel
                      control={
                        <Checkbox 
                          name="acceptPrivacy" 
                          checked={formData.acceptPrivacy} 
                          onChange={handleCheckboxChange}
                          required
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {t('signup.privacy.accept', 'Acepto la ')}
                          <Link href="/privacy" target="_blank">{t('signup.privacy.name', 'Política de Privacidad')}</Link>
                        </Typography>
                      }
                    />
                    
                    <FormControlLabel
                      control={
                        <Checkbox 
                          name="acceptMarketing" 
                          checked={formData.acceptMarketing} 
                          onChange={handleCheckboxChange}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {t('signup.terms.acceptMarketing', 'Acepto recibir información comercial y promociones')}
                        </Typography>
                      }
                    />
                  </Box>
                )}
              </Box>
            </DialogContent>
            
            <DialogActions sx={{ px: 3, pb: 3 }}>
              {activeTab === 0 ? (
                <>
                  <Button 
                    variant="contained" 
                    fullWidth 
                    size="large" 
                    type="submit"
                    form="auth-form"
                    disabled={loading}
                    sx={{ 
                      py: 1.5,
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'primary.dark'
                      }
                    }}
                  >
                    {loading ? <CircularProgress size={24} /> : t('header.login')}
                  </Button>
                </>
              ) : (
                <Button 
                  variant="contained" 
                  fullWidth 
                  size="large" 
                  type="submit"
                  form="auth-form"
                  disabled={loading}
                  sx={{ 
                    py: 1.5,
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark'
                    }
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : t('header.register')}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
      
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ModalPortal>
  );
};

export default AuthModal; 